// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateInfoRow } from '@/components/date-info-row';
import { LabeledTruncatedText } from '@/components/labeled-truncated-text';
import { Link } from '@/components/link';
import { LocationPairCell } from '@/components/location-pair-cell';
import { StatusChip } from '@/components/status-chip';
import {
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  RETURN_ORDER_REASON_LABELS,
  ReturnOrder,
} from '@/return-order/api/types';
import {
  PHASE_CONFIG,
  ParsedStatus,
  RO_STATUS_TOOLTIPS,
  getModifierVariant,
  isCancelled,
  parseStatus,
} from '@/return-order/constants/status';
import { getStatusIcon } from '@/return-order/constants/status-icons';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { Fragment, MouseEvent } from 'react';

/**
 * Props for the ReturnOrderTableRow component.
 */
interface Props {
  returnOrder: ReturnOrder;
}

/**
 * Creates tooltip content showing all return order dates
 */
function getAllReturnOrderDatesContent(
  returnOrder: ReturnOrder,
  parsed: ParsedStatus
) {
  const fullyReceived =
    parsed.phase === 'received' &&
    (parsed.modifier === 'full' || parsed.modifier === 'overage');

  const dates = [
    {
      label: 'Created:',
      date:
        returnOrder.createdAt?.toLocaleString({ dateStyle: 'medium' }) || null,
    },
    {
      label: 'Promise ship:',
      date:
        returnOrder.promiseShipDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Promise delivery:',
      date:
        returnOrder.promiseDeliveryDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    ...(returnOrder.mostRecentShippedAt
      ? [
          {
            label: fullyReceived ? 'Shipped:' : 'Most recent ship:',
            date: returnOrder.mostRecentShippedAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(returnOrder.mostRecentDeliveredAt
      ? [
          {
            label: fullyReceived ? 'Delivered:' : 'Most recent deliver:',
            date: returnOrder.mostRecentDeliveredAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(returnOrder.mostRecentReceivedAt
      ? [
          {
            label: fullyReceived ? 'Received:' : 'Most recent receive:',
            date: returnOrder.mostRecentReceivedAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(returnOrder.cancelledAt
      ? [
          {
            label: 'Cancelled:',
            date:
              returnOrder.cancelledAt?.toLocaleString({
                dateStyle: 'medium',
              }) || null,
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={0.25}>
      {dates.map(({ label, date }) => (
        <DateInfoRow key={label} label={label} date={date} />
      ))}
    </Stack>
  );
}

/**
 * Gets the most relevant date for a return order based on its status.
 * Uses cascading priority rules based on RO lifecycle phase and modifier.
 */
function getRelevantDate(returnOrder: ReturnOrder, parsed: ParsedStatus) {
  const { phase, modifier } = parsed;

  // 1. Cancelled — show cancelled date
  if (isCancelled(returnOrder.status) && returnOrder.cancelledAt) {
    return {
      date: returnOrder.cancelledAt.toLocaleString({ dateStyle: 'medium' }),
      label: null,
    };
  }

  // 2. Fully/overage received — show latest received date
  if (phase === 'received' && (modifier === 'full' || modifier === 'overage')) {
    if (returnOrder.mostRecentReceivedAt) {
      return {
        date: returnOrder.mostRecentReceivedAt.toLocaleString({
          dateStyle: 'medium',
        }),
        label: null,
      };
    }
  }

  // 3. Partial received — show promise delivery or promise ship
  if (phase === 'received' && modifier === 'partial') {
    if (returnOrder.promiseDeliveryDate) {
      return {
        date: returnOrder.promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }
    if (returnOrder.promiseShipDate) {
      return {
        date: returnOrder.promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }
  }

  // 4. Fully/overage delivered — show latest delivered date
  if (
    phase === 'delivered' &&
    (modifier === 'full' || modifier === 'overage')
  ) {
    if (returnOrder.mostRecentDeliveredAt) {
      return {
        date: returnOrder.mostRecentDeliveredAt.toLocaleString({
          dateStyle: 'medium',
        }),
        label: null,
      };
    }
  }

  // 5. Partial delivered — show promise delivery or promise ship
  if (phase === 'delivered' && modifier === 'partial') {
    if (returnOrder.promiseDeliveryDate) {
      return {
        date: returnOrder.promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }
    if (returnOrder.promiseShipDate) {
      return {
        date: returnOrder.promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }
  }

  // 6. Promise delivery date (for draft, awaiting_3pl, open phases)
  if (returnOrder.promiseDeliveryDate) {
    return {
      date: returnOrder.promiseDeliveryDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise delivery',
    };
  }

  // 7. Shipped phase — show latest shipped date
  if (phase === 'shipped' && returnOrder.mostRecentShippedAt) {
    return {
      date: returnOrder.mostRecentShippedAt.toLocaleString({
        dateStyle: 'medium',
      }),
      label: null,
    };
  }

  // 8. Promise ship date
  if (returnOrder.promiseShipDate) {
    return {
      date: returnOrder.promiseShipDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise ship',
    };
  }

  // 9. Updated != Created — show updated date
  if (
    returnOrder.lastUpdatedAt.toMillis() !== returnOrder.createdAt.toMillis()
  ) {
    return {
      date: returnOrder.lastUpdatedAt.toLocaleString({ dateStyle: 'medium' }),
      label: 'Updated',
    };
  }

  // 10. Created date (fallback)
  return {
    date: returnOrder.createdAt.toLocaleString({ dateStyle: 'medium' }),
    label: 'Created',
  };
}

/**
 * A table row component for displaying return order information.
 */
export function ReturnOrderTableRow(props: Props) {
  const navigate = useNavigate();
  const parsedStatus = parseStatus(props.returnOrder.status);

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.metaKey || event.ctrlKey) {
      window.open(`/return-orders/${props.returnOrder.key}`, '_blank');
    } else {
      navigate({
        to: '/return-orders/$returnOrderKey',
        params: { returnOrderKey: props.returnOrder.key },
      });
    }
  };

  return (
    <TableRow onClick={handleRowClick} hover sx={{ cursor: 'pointer' }}>
      {/* References Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">
            {props.returnOrder.returnOrderNumber}
          </Typography>
          {props.returnOrder.exchangeGroup && (
            <Tooltip
              arrow
              placement="right"
              title={
                <Box>
                  Part of exchange{' '}
                  <b>{props.returnOrder.exchangeGroup.exchangeNumber}</b> <br />
                  {props.returnOrder.exchangeGroup.fulfillmentOrders.length >
                    0 && (
                    <>
                      {' (linked to '}
                      {props.returnOrder.exchangeGroup.fulfillmentOrders.map(
                        (fo, index) => (
                          <Fragment key={fo.key}>
                            {index > 0 && ', '}
                            <Link
                              to="/fulfillments/$fulfillmentKey"
                              params={{ fulfillmentKey: fo.key }}
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: 'common.white',
                                textDecoration: 'underline',
                              }}
                            >
                              {fo.fulfillmentOrderNumber}
                            </Link>
                          </Fragment>
                        )
                      )}
                      {')'}
                    </>
                  )}
                </Box>
              }
            >
              <Chip label="Exchange" size="small" />
            </Tooltip>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={0.3}
        >
          <Typography variant="subtitle2">Type:</Typography>
          {RETURN_ORDER_REASON_LABELS[props.returnOrder.reason] ? (
            <Typography variant="body2">
              {RETURN_ORDER_REASON_LABELS[props.returnOrder.reason]}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          )}
        </Stack>
        {props.returnOrder.returnReason ? (
          <LabeledTruncatedText
            label="Reason:"
            text={props.returnOrder.returnReason}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">Reason:</Typography>
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          </Stack>
        )}
        {props.returnOrder.orderNotes ? (
          <LabeledTruncatedText
            label="Note:"
            text={props.returnOrder.orderNotes}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">Note:</Typography>
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          </Stack>
        )}
      </TableCell>

      {/* From/To Location Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <LocationPairCell
          fromLocation={props.returnOrder.fromLocation}
          toLocation={props.returnOrder.toLocation}
          showThirdPartyChip
        />
      </TableCell>

      {/* Items Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack spacing={0.3}>
          {props.returnOrder.items
            .slice(0, props.returnOrder.items.length > 4 ? 3 : 4)
            .map((item) => {
              const ItemIcon = !item.item.hasAssets
                ? SettingsOutlined
                : Inventory2Outlined;
              return (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={0.3}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    <ItemIcon sx={{ fontSize: '1rem' }} />
                    <>
                      <Typography variant="body2">{item.item.sku}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({item.item.name})
                      </Typography>
                    </>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      &times; {formatNumber(item.requestedQuantity)}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          {props.returnOrder.items.length > 4 && (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.5}>
                  {props.returnOrder.items.slice(3).map((item) => {
                    const itemLabel = `${item.item.sku} (${item.item.name})`;
                    return (
                      <Typography key={item.id} variant="caption">
                        {itemLabel} &times;{' '}
                        {formatNumber(item.requestedQuantity)}
                      </Typography>
                    );
                  })}
                </Stack>
              }
            >
              <Typography variant="body2" color="text.secondary">
                +{props.returnOrder.items.length - 3} more
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      {/* Status Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
            width="100%"
          >
            <Tooltip title={RO_STATUS_TOOLTIPS[props.returnOrder.status]} arrow>
              <StatusChip
                icon={getStatusIcon(parsedStatus.phase, parsedStatus.modifier)}
                label={PHASE_CONFIG[parsedStatus.phase].label}
                color={PHASE_CONFIG[parsedStatus.phase].chipConfig.color}
                variant={getModifierVariant(
                  parsedStatus.phase,
                  parsedStatus.modifier
                )}
              />
            </Tooltip>
            {(() => {
              const relevantDate = getRelevantDate(
                props.returnOrder,
                parsedStatus
              );
              if (relevantDate.date) {
                return (
                  <Tooltip
                    arrow
                    placement="left"
                    title={getAllReturnOrderDatesContent(
                      props.returnOrder,
                      parsedStatus
                    )}
                  >
                    <Stack alignItems="flex-end">
                      <Typography variant="body2" color="text.secondary">
                        {relevantDate.label && `${relevantDate.label}`}
                      </Typography>
                      <Typography variant="body2">
                        {relevantDate.date}
                      </Typography>
                    </Stack>
                  </Tooltip>
                );
              }
              return (
                <Tooltip
                  arrow
                  placement="left"
                  title={getAllReturnOrderDatesContent(
                    props.returnOrder,
                    parsedStatus
                  )}
                >
                  <Typography variant="body2" color="text.secondary">
                    No date set
                  </Typography>
                </Tooltip>
              );
            })()}
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
